import React, { useCallback, useEffect, useState } from "react";
import { connect } from "react-redux";
import NDISClaimsReportsPresenter from "./NDISClaimsReportsPresenter";
import { apiGetRequest, apiPostRequest } from "helpers/Requests";
import {
  URL_REPORTING_NDIS_PARTICIPANT_LIST,
  URL_REPORTING_NDIS_REPORTS,
} from "store/services/URL";
import { emptyPaginationObj, SORT_TYPE } from "Constants/constant";
import { getSpSetting } from "store/selector/sp.selector";
import { toast } from "react-toastify";
import useReportExport from "../useReportExport";
import { withReportSortOnUrl } from "../reportExportUtils";

const mapStateToProps = (state) => ({
  settings: getSpSetting(state),
});

const NDISClaimsReportsContainer = (props) => {
  const { settings } = props;
  const [search, setSearch] = useState(null);
  const [selectedParticipant, setSelectedParticipant] = useState(null);
  const [participantList, setParticipant] = useState([]);
  const [fromDate, setFromDate] = useState(null);
  const [toDate, setToDate] = useState(null);
  const [selectedFromDate, setSelectedFromDate] = useState(null);
  const [page, setPage] = useState(1);
  const [paginationObj, setPaginationObj] = useState(emptyPaginationObj);
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [participantLoader, setParticipantLoader] = useState(false);
  const [checkboxes, setCheckboxes] = useState([]);
  const [allSelected, setAllSelected] = useState(false);
  const [isDisableSearch, setIsDisableSearch] = useState(false);
  const [sortDescending, setSortDescending] = useState(
    paginationObj.sort_order === SORT_TYPE.DESC ? true : false
  );
  const [sortBy, setSortBy] = useState(paginationObj.sort_field);
  const { runExport, QueuedModal } = useReportExport(
    "ndis_claim_report",
    "NDIS Claims Report",
    { sortBy, sortDescending }
  );

  useEffect(() => {
    setParticipantLoader(true);
    apiGetRequest(URL_REPORTING_NDIS_PARTICIPANT_LIST)
      .then((response) => {
        const data = response.data?.data?.map((item) => ({
          label: `${item.first_name} ${item.last_name}`,
          value: item.id,
        }));
        setParticipant(data || []);
      })
      .catch((error) => {
        console.log("error", error);
      })
      .finally(() => {
        setParticipantLoader(false);
      });
  }, []);

  const buildPayload = (isClearAll = false) => ({
    search: search ? search : "",
    participant: isClearAll
      ? "all"
      : selectedParticipant?.length > 0
      ? selectedParticipant?.map((item) => item?.value)
      : "all",
    service_date: toDate && fromDate ? [fromDate, toDate] : "all",
  });

  const fetchListReports = useCallback(
    async (
      sort_field = null,
      sort_order = null,
      report = null,
      comingPage,
      isClearAll = false
    ) => {
      if (!toDate && !fromDate && !isClearAll) {
        toast.warning("Please select date range");
        return;
      }

      setLoading(true);
      const pageToUse = comingPage ? comingPage : page;
      let url = `${URL_REPORTING_NDIS_REPORTS}?per_page=25&page=${pageToUse}`;
      const filters = {
        sort_field: sort_field ?? sortBy,
        sort_order:
          sort_order ??
          (sortDescending ? SORT_TYPE.DESC : SORT_TYPE.ASC),
        page: pageToUse,
      };
      url = withReportSortOnUrl(url, filters, sortBy, sortDescending);

      const payload = buildPayload(isClearAll);

      if (report) {
        if (checkboxes && checkboxes.length > 0) {
          payload.ids = checkboxes;
        }
        await runExport({
          format: report,
          payload,
          syncUrl: url,
          filters,
          totalRows: paginationObj?.total,
          setLoader: setLoading,
          onSyncExcel: (downloadUrl) => window.open(downloadUrl, "_blank"),
          onSyncPdf: (downloadUrl) => window.open(downloadUrl, "_blank"),
        });
        return;
      }

      apiPostRequest(url, payload)
        .then((res) => {
          setData(res?.data?.data?.invoices?.data || []);
          setPaginationObj({
            total: res?.data?.data?.invoices?.total || 0,
            per_page: res?.data?.data?.invoices?.per_page || 25,
            current_page: res?.data?.data?.invoices?.current_page || 1,
            totalPages: res?.data?.data?.invoices?.total_pages || 0,
          });
        })
        .catch((err) => {
          console.log("err", err);
        })
        .finally(() => {
          setLoading(false);
        });
    },
    [
      selectedParticipant,
      toDate,
      fromDate,
      page,
      search,
      checkboxes,
      sortBy,
      sortDescending,
      paginationObj?.total,
      runExport,
    ]
  );

  const handleSearch = () => {
    setPage(1);
    setPaginationObj((prev) => ({ ...prev, page: 1 }));
    fetchListReports(
      sortBy,
      sortDescending ? SORT_TYPE.DESC : SORT_TYPE.ASC,
      null,
      1
    );
  };

  const handleClearFilters = () => {
    setSearch(null);
    setData([]);
    setPage(1);
    setSelectedParticipant(null);
    setFromDate(null);
    setToDate(null);
    setSelectedFromDate(null);
    setCheckboxes([]);
    setAllSelected(false);
    setPaginationObj(emptyPaginationObj);
  };

  const handleFileDownLoad = (format) => {
    fetchListReports(
      sortBy,
      sortDescending ? SORT_TYPE.DESC : SORT_TYPE.ASC,
      format,
      page
    );
  };

  const handleCheckboxChange = (e, uuid) => {
    setCheckboxes((prevCheckboxes) => {
      const filteredList = prevCheckboxes.filter((c) => c !== uuid);
      if (e) {
        filteredList.push(uuid);
      }
      return filteredList;
    });
  };

  const toggleCheckboxes = (val) => {
    setCheckboxes(val ? data?.map((el) => el?.uuid) : []);
  };

  useEffect(() => {
    if (
      checkboxes &&
      checkboxes?.length === data?.length &&
      data?.length !== 0
    ) {
      setAllSelected(true);
    } else {
      setAllSelected(false);
    }
  }, [checkboxes, data?.length]);

  const flipSort = (by) => {
    setSortBy(by);
    setSortDescending(!sortDescending);
    fetchListReports(by, !sortDescending ? SORT_TYPE.DESC : SORT_TYPE.ASC);
  };

  return (
    <NDISClaimsReportsPresenter
      settings={settings}
      search={search}
      setSearch={setSearch}
      participantList={participantList}
      selectedParticipant={selectedParticipant}
      setSelectedParticipant={setSelectedParticipant}
      setFromDate={setFromDate}
      setToDate={setToDate}
      fromDate={fromDate}
      toDate={toDate}
      selectedFromDate={selectedFromDate}
      setSelectedFromDate={setSelectedFromDate}
      fetchListReports={fetchListReports}
      handleSearch={handleSearch}
      handleClearFilters={handleClearFilters}
      handleFileDownLoad={handleFileDownLoad}
      page={page}
      setPage={setPage}
      data={data}
      loading={loading}
      loader={participantLoader}
      setAllSelected={setAllSelected}
      allSelected={allSelected}
      checkboxes={checkboxes}
      setCheckboxes={setCheckboxes}
      handleCheckboxChange={handleCheckboxChange}
      toggleCheckboxes={toggleCheckboxes}
      paginationObj={paginationObj}
      setPaginationObj={setPaginationObj}
      sortDescending={sortDescending}
      setSortDescending={setSortDescending}
      sortBy={sortBy}
      setSortBy={setSortBy}
      flipSort={flipSort}
      setData={setData}
      isDisableSearch={isDisableSearch}
      setIsDisableSearch={setIsDisableSearch}
      QueuedModal={QueuedModal}
    />
  );
};

export default connect(mapStateToProps)(NDISClaimsReportsContainer);
